import { logger } from "c7-logger";

import { removeNonNumber } from "cc-tools/helpers";

import {
  ExperienceType,
  PayRequestPayload,
} from "checkout-query-client/api/types";
import { MerchantSettings } from "checkout-query-client/hooks/use-merchant-settings";
import { usePubSub } from "checkout-query-client/hooks/use-pub-sub";
import {
  PubSubMessage,
  PubSubNamespace,
} from "checkout-query-client/publish-subcriber/types";
import {
  getAcceptedMandates,
  getDeviceFingerprintSessionId,
} from "checkout-query-client/utils/helpers";

import { IEncryptData, valueEncrypt } from "encrypt-value/encrypt";

import {
  BrowserInfo,
  CardType,
  EncryptedCard,
  PayRequest,
  PayResponse_Status,
  PaymentMethod,
  PaymentMethod_Type,
} from "frontend-client";

export const buildGiftCardPayload = async (
  payload: PayRequestPayload
): Promise<PayRequest> => {
  const { formFields, merchantSettings, experienceType, orderDigest } = payload;
  const request = new PayRequest();
  const encryptedCard = new EncryptedCard();
  const paymentMethod = new PaymentMethod();
  const browserInfo = new BrowserInfo();

  if (formFields && merchantSettings) {
    const giftCardPayload = await getGiftCardPayload(
      merchantSettings,
      formFields,
      experienceType
    );

    if (
      giftCardPayload.status === PayResponse_Status.SUCCESS &&
      giftCardPayload.giftCard
    ) {
      encryptedCard.encryptedPan = giftCardPayload.giftCard.encryptedPan;
      encryptedCard.encryptionKeyId = giftCardPayload.giftCard.encryptionKeyId;
      encryptedCard.pinNumber = formFields?.pinCode ?? "";
    }

    encryptedCard.cardType = CardType.GIFT;
    paymentMethod.card = encryptedCard;
    paymentMethod.type = PaymentMethod_Type.GIFT_CARD;
    request.paymentMethod = paymentMethod;
    request.acceptedMandates = getAcceptedMandates(formFields);

    const date = new Date();
    browserInfo.acceptHeader = "text/html;charset=UTF-8";
    browserInfo.language = window.navigator.language || "";
    browserInfo.colorDepth = window.screen.colorDepth || 0;
    browserInfo.screenHeight = window.screen.height || 0;
    browserInfo.screenWidth = window.screen.width || 0;
    browserInfo.timezoneOffset = date.getTimezoneOffset() || 0;
    request.browserInfo = browserInfo;
    request.orderDigest = orderDigest ?? "";
    request.deviceFingerprintSessionId = getDeviceFingerprintSessionId();
  }
  return request;
};

const getGiftCardPayload = async (
  merchantSettings: MerchantSettings,
  formFields: Record<string, string>,
  experienceType?: ExperienceType
) => {
  let encryptedPanData: IEncryptData;
  try {
    if (experienceType === "dropInUI") {
      encryptedPanData = {
        encryptedData: formFields?.giftCardNumber || "",
        encryptionNonce: "",
        encryptionTimestamp: 0,
      };
    } else {
      encryptedPanData = await valueEncrypt({
        value: removeNonNumber(formFields?.giftCardNumber ?? ""),
        key: merchantSettings?.encryptionKey ?? "",
      });
    }
    return {
      status: PayResponse_Status.SUCCESS,
      giftCard: {
        encryptedPan: encryptedPanData.encryptedData,
        encryptionKeyId: merchantSettings?.encryptionKeyId ?? "",
      },
    };
  } catch (e) {
    logger.error(PubSubMessage.GiftCardEncryptionError, e);
    usePubSub().publish({
      level: "error",
      namespace: PubSubNamespace.payment,
      message: PubSubMessage.GiftCardEncryptionError,
    });
    return { status: PayResponse_Status.UNSPECIFIED };
  }
};
